import type {
  RenderCaption,
  RenderCenterTransform,
  RenderClip,
  RenderDrawing,
  RenderEffect,
  RenderJob,
  RenderSource,
  RenderSourceKind,
  RenderSticker,
  RenderTextDesign,
  RenderTimeline,
  RenderTrack,
  RenderTrackKind,
  RenderTransform
} from '../../../core/render-runtime/worker/render-job.model';
import { fontSpecsForCaptions, fontSpecsForTextDesigns } from '../../../core/render-runtime/worker/render-font-assets';
import type { EditorAsset } from '../core/models/editor-asset.model';
import {
  effectiveScaleXY,
  type AnimateSpec,
  type EditorBeatParams,
  type EditorClip,
  type EditorEffectParams,
  type EditorStickerParams,
  type EditorTextStyle,
  type EditorTransform,
  type EditorTransitionParams,
  type TransformKeyframe
} from '../core/models/editor-clip.model';
import type { EditorProject } from '../core/models/editor-project.model';
import { isTrackAudible } from '../core/utils/track-flags.util';
import { resolveEditorTextDesignText } from '../core/utils/text-design-text.util';
import { withoutUnwantedTextDesignSurface } from '../core/utils/text-design-surface.util';
import { effectiveMotionTrackedBlurRegions, effectiveMotionTrackingKeyframes } from '../core/utils/motion-tracking.util';
import {
  isEffectSceneProgram,
  type EffectSceneProgram,
} from '../../../core/render-runtime/shared/effect-scene-program';

/**
 * editor-project-render-job.adapter — pure mapping from the super-editor's
 * `EditorProject` (Review editor, Template-Use, Template-Creation) to the
 * unified, serializable `RenderJob` consumed by the background render worker.
 *
 * It is a PURE function: no DOM, no decode, no I/O. Media is carried as a Blob
 * (preferred — already in memory, transferable) or a URL on `RenderSource`; the
 * worker probes real durations/sizes via mediabunny.
 *
 * ---------------------------------------------------------------------------
 * MAPPING OVERVIEW — nothing is dropped
 * ---------------------------------------------------------------------------
 * ASSETS → RenderSource[]
 *   - Every `project.assets` entry with a usable url becomes a RenderSource.
 *     The asset's in-memory `objectUrl` (a blob: URL) is downgraded to a Blob
 *     when it is a real Blob handle; otherwise the durable `sourceUrl` (or the
 *     blob: URL) is carried as `url`. Kind = the closest RenderSource kind
 *     (video/image/audio); text/caption/effect/sticker assets have no media and
 *     are skipped as sources (their clips render as captions/effects/stickers).
 *
 * TRACKS / CLIPS
 *   - Every `video`/`audio`/`overlay` track → a RenderTrack of the same kind
 *     (editor `beat`/`effect`/`caption` tracks carry no media clips — their
 *     visual clips are mapped to the timeline-level caption/effect/sticker lists
 *     instead, mirroring the worker compositor's draw model).
 *   - Each media clip (`video`/`image`/`audio`) → a RenderClip carrying the
 *     timeline window (`startSeconds`/`endSeconds`), the source cut window
 *     (`sourceStartSeconds`/`sourceEndSeconds`, video/audio only), `playbackRate`,
 *     `volume`/`muted`, and `transform` (when the clip has a non-identity
 *     EditorTransform — center-offset + scale + rotation + opacity is converted
 *     to the worker's normalized top-left rect).
 *
 * CAPTIONS / TEXT → RenderCaption[]
 *   - `text`/`caption` clips → RenderCaption with the window, normalized anchor
 *     (from the EditorTransform center-offset, else the editor's default caption
 *     baseline), font size/weight/family/color/background/alignment from
 *     `textStyle` (the same fields the worker's drawCaption reads).
 *
 * STICKERS → RenderSticker[] (image overlay) or RenderEffect (primitive)
 *   - A `sticker` clip whose asset is an actual image → RenderSticker (image
 *     overlay honouring transform + an `animation` hint).
 *   - A primitive sticker clip (emoji/badge/callout/strip/arrow drawn from
 *     `clip.sticker`, no image source) → RenderEffect carrying the sticker
 *     primitive params (`family:'sticker'`), so nothing is dropped.
 *
 * EFFECTS / TRANSITIONS / BEATS → RenderEffect[]
 *   - `effect`/`transition`/`beat` clips → RenderEffect with `params.family`
 *     (effect|transition|beat) + `params.primitive` + every primitive param
 *     (color/color2/opacity/speed/size/angle/blend/intensity). The worker's
 *     classifyEffect routes on `family`, and the family draw util reads those
 *     exact param keys.
 *
 * OUTPUT
 *   - width/height/fps/bitrate from `project.exportSettings`. videoCodec 'avc'
 *     (most compatible) and a single cover thumbnail are requested.
 */

const DEFAULT_FPS = 30;
const DEFAULT_BITRATE = 8_000_000;
const DEFAULT_WIDTH = 1080;
const DEFAULT_HEIGHT = 1920;
const EPSILON = 0.05;

/** Editor track kinds that carry placeable MEDIA clips (mapped to RenderTrack). */
const MEDIA_TRACK_KINDS = new Set<RenderTrackKind>(['video', 'overlay', 'audio']);

/**
 * buildEditorRenderJob — maps a super-editor `EditorProject` to a RenderJob.
 * Pure and deterministic (modulo the generated id when the project id is blank).
 *
 * `thumbnailCount` (optional, default 1, clamped 1..15) requests N evenly-spaced
 * cover thumbnails from the worker; omitting it yields the single cover thumbnail
 * the editor flow produced before, byte-for-byte.
 */
export function buildEditorRenderJob(project: EditorProject, thumbnailCount = 1): RenderJob {
  const settings = project.exportSettings ?? null;
  const width = positiveOr(settings?.width, DEFAULT_WIDTH);
  const height = positiveOr(settings?.height, DEFAULT_HEIGHT);

  // ---- Sources (assets) ----
  const assetById = new Map<string, EditorAsset>();
  const assetIdToSourceId = new Map<string, string>();
  const sources: RenderSource[] = [];

  for (const asset of project.assets ?? []) {
    if (!asset?.id) continue;
    assetById.set(asset.id, asset);
    const source = buildSource(asset, sources.length);
    if (!source) continue; // text/effect/sticker assets with no media — no source.
    assetIdToSourceId.set(asset.id, source.id);
    sources.push(source);
  }

  // Scene plates are not timeline media clips, but the render worker must decode
  // them through the same source pipeline. Add only plates referenced by an effect
  // in this project; unused catalogue scenes remain completely lazy.
  const effectSceneSourceIdByUrl = new Map<string, string>();
  for (const track of project.tracks ?? []) {
    for (const clip of track.clips ?? []) {
      const effect = clip.effect as EditorEffectParams | null | undefined;
      const url = effect?.sceneAssetUrl?.trim();
      if (!url || effectSceneSourceIdByUrl.has(url)) continue;
      const id = `effect-scene-${effectSceneSourceIdByUrl.size + 1}`;
      effectSceneSourceIdByUrl.set(url, id);
      sources.push({
        id,
        kind: 'image',
        url,
        width: positiveOr(effect?.sceneAssetWidth, 1672),
        height: positiveOr(effect?.sceneAssetHeight, 941),
      });
    }
  }

  // ---- Tracks / clips + timeline-level captions / stickers / effects ----
  const tracks: RenderTrack[] = [];
  const captions: RenderCaption[] = [];
  const textDesigns: RenderTextDesign[] = [];
  const drawings: RenderDrawing[] = [];
  const stickers: RenderSticker[] = [];
  const effects: RenderEffect[] = [];
  const motions: { startSeconds: number; endSeconds: number; keyframes: TransformKeyframe[] }[] = [];
  let timelineDurationSeconds = 0;
  let captionIndex = 0;
  let stickerIndex = 0;
  let effectIndex = 0;

  const allTracks = project.tracks ?? [];
  for (const [trackIndex, track] of allTracks.entries()) {
    // HIDDEN track ⇒ omitted from the output entirely (its visual clips are not drawn, its
    // captions/stickers/effects are not emitted, and its audio is silenced via isTrackAudible).
    if (track.hidden) continue;
    // Per-track audio routing — a clip on a non-audible track (muted, or a non-soloed track
    // while another is soloed) is forced muted so the worker drops its audio. Mirrors the
    // preview's audibleSourceClipIds gate so preview audio == export audio.
    const trackAudible = isTrackAudible(track, allTracks);

    const renderTrackKind = mapTrackKind(track.kind);
    const renderTrack: RenderTrack = renderTrackKind && MEDIA_TRACK_KINDS.has(renderTrackKind)
      ? { id: track.id, kind: renderTrackKind, z: trackIndex, clips: [] }
      : { id: track.id, kind: 'video', z: trackIndex, clips: [] };
    let trackHasMediaClips = false;

    for (const clip of track.clips ?? []) {
      const start = finiteOr(clip.startSeconds, 0);
      const end = Math.max(start + EPSILON, finiteOr(clip.endSeconds, start + EPSILON));
      timelineDurationSeconds = Math.max(timelineDurationSeconds, end);

      switch (clip.kind) {
        case 'video':
        case 'image':
        case 'audio': {
          const renderClip = buildMediaClip(
            clip,
            start,
            end,
            width,
            height,
            assetIdToSourceId,
            assetById,
            track.kind === 'overlay',
          );
          if (renderClip) {
            // Force-mute clips whose track isn't audible (the worker drops muted clips' audio).
            if (!trackAudible) renderClip.muted = true;
            renderTrack.clips.push(renderClip);
            trackHasMediaClips = true;
          }
          break;
        }
        case 'text':
        case 'caption': {
          if (clip.kind === 'text' && clip.textDesign) {
            textDesigns.push(buildTextDesign(clip, start, end));
          } else {
            const caption = buildCaption(clip, start, end, captionIndex++);
            if (caption) captions.push({ ...caption, z: trackIndex });
          }
          break;
        }
        case 'drawing': {
          const drawing = buildDrawing(clip, start, end);
          if (drawing) drawings.push(drawing);
          break;
        }
        case 'sticker': {
          const asset = clip.assetId ? assetById.get(clip.assetId) : undefined;
          const imageSourceId = isImageAsset(asset) && clip.assetId ? assetIdToSourceId.get(clip.assetId) : undefined;
          if (imageSourceId) {
            stickers.push(buildImageSticker(clip, start, end, imageSourceId, stickerIndex++, width, height));
          } else {
            // Primitive sticker (emoji/badge/callout/…): carry as an effect so
            // its params survive the trip; the worker has no image to draw.
            effects.push(buildStickerEffect(clip, start, end, effectIndex++));
          }
          break;
        }
        case 'effect':
        case 'transition':
        case 'beat': {
          effects.push(buildEffect(
            clip,
            start,
            end,
            effectIndex++,
            effectSceneSourceIdByUrl,
          ));
          break;
        }
        case 'motion': {
          // MOTION clip (Animate tool) on an 'animation' track: no media, carries
          // keyframes (authored around IDENTITY) that animate the BASE visual frame
          // during [start,end]. Collect into the timeline-level motions list; the
          // compositor composes all active motions onto the base clip. A motion with no
          // usable keyframes is a no-op, so it is skipped (keeps the fast path clean).
          const motion = buildMotion(clip, start, end);
          if (motion) motions.push(motion);
          break;
        }
        default:
          break;
      }
    }

    if (trackHasMediaClips) tracks.push(renderTrack);
  }

  // Render length = the end of the LAST element across ALL tracks (clips/captions/stickers/
  // effects all extended `timelineDurationSeconds` above) — matching the store's selectDuration,
  // so preview end == export end. The STORED project.durationSeconds is only a fallback for a
  // project with no clips; letting it win cut renders short (or padded them) whenever the user
  // extended/trimmed the timeline after load.
  const durationSeconds = round3(Math.max(EPSILON, timelineDurationSeconds || project.durationSeconds || EPSILON));

  // Synthesize the global ducking curve from clips flagged `duckUnderVoice` (music)
  // against the voice/dialogue windows. Empty (no flag / no voice) → omitted → the
  // mixer applies no ducking → audio identical to today.
  const audioDucking = buildAudioDucking(project, durationSeconds);

  const timeline: RenderTimeline = {
    durationSeconds,
    tracks,
    ...(captions.length ? { captions } : {}),
    ...(textDesigns.length ? { textDesigns } : {}),
    ...(drawings.length ? { drawings } : {}),
    ...(stickers.length ? { stickers } : {}),
    ...(effects.length ? { effects } : {}),
    ...(motions.length ? { motions } : {}),
    ...(audioDucking ? { audioDucking } : {})
  };

  // Custom fonts the burned captions need (e.g. the Urdu face). Empty for
  // Latin-only captions → no `fonts` on the job → output unchanged.
  const fonts = dedupeFontSpecs([
    ...fontSpecsForCaptions(captions),
    ...fontSpecsForTextDesigns(textDesigns),
  ]);

  return {
    id: project.id || generateId(),
    origin: editorOrigin(project),
    sources,
    timeline,
    output: {
      width,
      height,
      fps: positiveOr(settings?.fps, DEFAULT_FPS),
      bitrate: positiveOr(settings?.bitrate, DEFAULT_BITRATE),
      videoCodec: 'avc',
      thumbnailCount: clampThumbnailCount(thumbnailCount),
      ...(fonts.length ? { fonts } : {})
    }
  };
}

/** drawing clip -> serializable render-layer payload. */
function buildDrawing(clip: EditorClip, start: number, end: number): RenderDrawing | null {
  const layer = clip.drawing;
  if (!layer?.strokes?.length) return null;

  const drawing: RenderDrawing = {
    id: clip.id,
    startSeconds: round3(start),
    endSeconds: round3(end),
    layer: {
      id: layer.id,
      version: 1,
      strokes: layer.strokes.map(stroke => ({
        id: stroke.id,
        tool: stroke.tool,
        color: stroke.color,
        weight: stroke.weight,
        opacity: stroke.opacity,
        composite: stroke.tool === 'eraser' ? 'destination-out' : 'source-over',
        points: stroke.points.map(point => ({
          x: point.x,
          y: point.y,
          ...(point.pressure !== undefined ? { pressure: point.pressure } : {}),
          ...(point.timeMs !== undefined ? { timeMs: point.timeMs } : {}),
        })),
      })),
    },
  };

  // Drawing points are authored directly in normalized canvas space. Keep the layer
  // canvas-anchored so subsequent pen/eraser/select gestures use the same coordinates;
  // whole-clip transforms would require inverse-transforming every input gesture.
  return drawing;
}

function buildTextDesign(clip: EditorClip, start: number, end: number): RenderTextDesign {
  const design: RenderTextDesign = {
    id: clip.id,
    startSeconds: round3(start),
    endSeconds: round3(end),
    preset: withoutUnwantedTextDesignSurface(clip.textDesign!),
    text: resolveEditorTextDesignText(clip),
    primaryStyle: { ...(clip.textStyle ?? {}) },
    primaryColor: clip.color || undefined,
    textStyleKeyframes: clip.textStyleKeyframes?.map(point => ({ ...point })) ?? null,
  };
  const keyframes = mapTransformKeyframes(effectiveMotionTrackingKeyframes(clip));
  if (keyframes) design.keyframes = keyframes;
  const animateIn = mapAnimateSpec(clip.animateIn);
  if (animateIn) design.animateIn = animateIn;
  const animateOut = mapAnimateSpec(clip.animateOut);
  if (animateOut) design.animateOut = animateOut;
  const baseTransform = mapCenterTransform(clip.transform);
  if (baseTransform) design.baseTransform = baseTransform;
  return design;
}

function dedupeFontSpecs<T extends { family: string }>(specs: readonly T[]): T[] {
  const seen = new Set<string>();
  return specs.filter(spec => {
    const key = spec.family.trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

/** template-use / template-create → 'template'; everything else → 'editor'. */
function editorOrigin(project: EditorProject): RenderJob['origin'] {
  return project.source === 'template' ? 'template' : 'editor';
}

/** Build a RenderSource for an asset, carrying its Blob (preferred) or url. */
function buildSource(asset: EditorAsset, index: number): RenderSource | null {
  const kind = mapSourceKind(asset.kind);
  if (!kind) return null;

  const blob = asBlob((asset.metadata as Record<string, unknown> | undefined)?.['blob']);
  const objectUrl = (asset.objectUrl || '').trim();
  const sourceUrl = (asset.sourceUrl || '').trim();
  // Prefer a Blob (transferable, no auth needed in the worker). Else carry a
  // durable URL — favour sourceUrl, but a blob: objectUrl is still resolvable
  // on the same thread/origin, so carry it when nothing else is present.
  const url = sourceUrl || objectUrl || null;
  if (!blob && !url) return null;

  return {
    id: `src-${kind}-${index}`,
    kind,
    blob: blob ?? null,
    url: blob ? null : url,
    durationSeconds: positiveOrNull(asset.durationSeconds),
    width: positiveOrNull(asset.width),
    height: positiveOrNull(asset.height)
  };
}

/** Map a media clip (video/image/audio) to a RenderClip; null when its source is missing. */
function buildMediaClip(
  clip: EditorClip,
  start: number,
  end: number,
  width: number,
  height: number,
  assetIdToSourceId: Map<string, string>,
  assetById: Map<string, EditorAsset>,
  allowOutsideCanvas = false,
): RenderClip | null {
  const sourceId = clip.assetId ? assetIdToSourceId.get(clip.assetId) : undefined;
  if (!sourceId) return null; // clip references an asset with no usable media.

  const kind: RenderClip['kind'] = clip.kind === 'image' ? 'image' : clip.kind === 'audio' ? 'audio' : 'video';
  const playbackRate = positiveOrNull(clip.playbackRate);

  const renderClip: RenderClip = {
    id: clip.id,
    sourceId,
    kind,
    startSeconds: round3(start),
    endSeconds: round3(end)
  };

  // Cut window into the source (video/audio only; images have no time window).
  if (kind !== 'image') {
    const sourceStart = numOrNull(clip.sourceStartSeconds);
    const sourceEnd = numOrNull(clip.sourceEndSeconds);
    if (sourceStart !== null) renderClip.sourceStartSeconds = round3(Math.max(0, sourceStart));
    if (sourceEnd !== null) renderClip.sourceEndSeconds = round3(Math.max(round3(renderClip.sourceStartSeconds ?? 0) + EPSILON, sourceEnd));
  }

  if (playbackRate !== null && playbackRate !== 1) renderClip.playbackRate = playbackRate;
  if (clip.speedCurve && clip.speedCurve.points && clip.speedCurve.points.length) {
    renderClip.speedCurve = { points: clip.speedCurve.points.map(p => ({ t: p.t, rate: p.rate, ...(p.ease ? { ease: p.ease } : {}) })) };
  }

  // Audio (rides along on visual clips too; the worker reads each clip's volume).
  const volume = numOrNull(clip.volume);
  if (volume !== null) renderClip.volume = Math.max(0, Math.min(2, volume));
  if (clip.muted) renderClip.muted = true;

  // Audio fades / volume envelope / duck flag (additive — omitted when unset, so the
  // mixer's gain stays constant volume = today's audio byte-for-byte).
  const fadeIn = positiveOrNull(clip.fadeInSeconds);
  if (fadeIn !== null) renderClip.fadeInSeconds = round3(fadeIn);
  const fadeOut = positiveOrNull(clip.fadeOutSeconds);
  if (fadeOut !== null) renderClip.fadeOutSeconds = round3(fadeOut);
  const envelope = mapVolumeEnvelope(clip.volumeEnvelope);
  if (envelope) renderClip.volumeEnvelope = envelope;
  if (clip.duckUnderVoice) renderClip.duckUnderVoice = true;
  if (clip.audioCleanup) {
    renderClip.audioCleanup = {
      ...clip.audioCleanup,
      noiseGate: clip.audioCleanup.noiseGate ? { ...clip.audioCleanup.noiseGate } : null,
      highPass: clip.audioCleanup.highPass ? { ...clip.audioCleanup.highPass } : null,
      voiceEnhancer: clip.audioCleanup.voiceEnhancer ? { ...clip.audioCleanup.voiceEnhancer } : null,
    };
  }

  // Visual placement (video/image only). Omitted = full-frame cover (the common case).
  if (kind !== 'audio') {
    const transform = mapTransform(clip.transform, width, height, clip.fitMode);
    if (transform) renderClip.transform = transform;

    // KEYFRAME + ENTER/EXIT animation — carried as PLAIN objects; the worker resolves
    // them per frame through the SHARED resolver (preview == export). Copied as-is
    // (channels are editor-space: x/y percent, scale multiplier, rotation deg, opacity
    // 0..1 — the same space the shared resolver operates in on the worker side). Absent
    // → omitted, so a clip with no animation renders byte-for-byte as today.
    const keyframes = mapTransformKeyframes(effectiveMotionTrackingKeyframes(clip));
    if (keyframes) renderClip.keyframes = keyframes;
    const animateIn = mapAnimateSpec(clip.animateIn);
    if (animateIn) renderClip.animateIn = animateIn;
    const animateOut = mapAnimateSpec(clip.animateOut);
    if (animateOut) renderClip.animateOut = animateOut;

    // Per-clip video ops (Crop / Adjust / Filters / Mask / Chroma) — carried only when authored,
    // so a plain clip stays byte-identical. Preview + worker apply them in lock-step.
    if (clip.crop) {
      renderClip.crop = { x: clamp01(clip.crop.x), y: clamp01(clip.crop.y), width: clamp01(clip.crop.width), height: clamp01(clip.crop.height) };
    }
    if (clip.adjust) renderClip.adjust = { ...clip.adjust };
    if (clip.adjustKeyframes?.length) {
      renderClip.adjustKeyframes = clip.adjustKeyframes.map(frame => ({ ...frame }));
    }
    if (clip.mediaEnhancements) {
      renderClip.mediaEnhancements = {
        blend: clip.mediaEnhancements.blend ? { ...clip.mediaEnhancements.blend } : null,
        stabilize: clip.mediaEnhancements.stabilize ? { ...clip.mediaEnhancements.stabilize } : null,
        enhanceQuality: clip.mediaEnhancements.enhanceQuality ? { ...clip.mediaEnhancements.enhanceQuality } : null,
        reduceNoise: clip.mediaEnhancements.reduceNoise ? { ...clip.mediaEnhancements.reduceNoise } : null,
        cameraTracking: clip.mediaEnhancements.cameraTracking ? { ...clip.mediaEnhancements.cameraTracking, customRegion: clip.mediaEnhancements.cameraTracking.customRegion ? { ...clip.mediaEnhancements.cameraTracking.customRegion } : null, processing: clip.mediaEnhancements.cameraTracking.processing ? { ...clip.mediaEnhancements.cameraTracking.processing } : null } : null,
        motionBlur: clip.mediaEnhancements.motionBlur ? { ...clip.mediaEnhancements.motionBlur, processing: clip.mediaEnhancements.motionBlur.processing ? { ...clip.mediaEnhancements.motionBlur.processing } : null } : null,
      };
    }
    if (clip.masks?.length) {
      renderClip.masks = clip.masks.filter(mask => !!mask?.shape).map(mask => ({
        ...mask,
        points: mask.points?.map(point => ({ ...point })),
        keyframes: mask.keyframes?.map(frame => ({ ...frame })) ?? null,
      }));
    } else if (clip.mask && clip.mask.shape) {
      renderClip.mask = {
        ...clip.mask,
        points: clip.mask.points?.map(point => ({ ...point })),
        keyframes: clip.mask.keyframes?.map(frame => ({ ...frame })) ?? null,
      };
    }
    if (clip.chroma && clip.chroma.color) renderClip.chroma = { ...clip.chroma };
    const blurRegions = effectiveMotionTrackedBlurRegions(clip);
    if (blurRegions?.length) {
      renderClip.blurRegions = blurRegions.map(r => ({ ...r }));
    }
  }

  return renderClip;
}

/** Sanitize a clip's volume envelope → sorted finite clip-relative points, or null. */
function mapVolumeEnvelope(
  raw: EditorClip['volumeEnvelope']
): NonNullable<RenderClip['volumeEnvelope']> | null {
  if (!raw || !raw.length) return null;
  const pts: NonNullable<RenderClip['volumeEnvelope']> = [];
  for (const r of raw) {
    const t = numOrNull(r?.tSeconds);
    const g = numOrNull(r?.gain);
    if (t === null || g === null) continue;
    pts.push({
      tSeconds: round3(Math.max(0, t)),
      gain: round3(Math.max(0, g)),
      ...(r.ease ? { ease: r.ease } : {}),
    });
  }
  if (!pts.length) return null;
  pts.sort((a, b) => a.tSeconds - b.tSeconds);
  return pts;
}

/**
 * Sanitize a clip's transform keyframes → finite clip-relative points carrying any
 * subset of channels (sorted by the resolver, which also sorts defensively). Null/
 * empty → null so the field is omitted (clip renders with its static transform).
 * Channels are passed through verbatim (already in the editor channel-space the
 * shared resolver expects); only the time and present numeric channels are kept.
 */
function mapTransformKeyframes(
  raw: TransformKeyframe[] | null | undefined
): TransformKeyframe[] | null {
  if (!raw || !raw.length) return null;
  const out: TransformKeyframe[] = [];
  for (const k of raw) {
    const t = numOrNull(k?.tSeconds);
    if (t === null) continue;
    const point: TransformKeyframe = { tSeconds: round3(Math.max(0, t)) };
    copyChannel(k, point, 'x');
    copyChannel(k, point, 'y');
    copyChannel(k, point, 'scale');
    copyChannel(k, point, 'scaleX');
    copyChannel(k, point, 'scaleY');
    copyChannel(k, point, 'rotation');
    copyChannel(k, point, 'opacity');
    if (Array.isArray(k?.boundedChannels) && k.boundedChannels.length) {
      point.boundedChannels = [...new Set(k.boundedChannels)];
    }
    if (k?.ease) point.ease = k.ease;
    out.push(point);
  }
  if (!out.length) return null;
  out.sort((a, b) => a.tSeconds - b.tSeconds);
  return out;
}

/** Copy one numeric channel from a raw keyframe to the sanitized one, if finite. */
function copyChannel(
  from: TransformKeyframe,
  to: TransformKeyframe,
  key: 'x' | 'y' | 'scale' | 'scaleX' | 'scaleY' | 'rotation' | 'opacity'
): void {
  const v = numOrNull(from?.[key]);
  if (v !== null) to[key] = round3(v);
}

/** Sanitize an enter/exit animation spec → a valid spec, or null when inactive. */
function mapAnimateSpec(raw: AnimateSpec | null | undefined): AnimateSpec | null {
  const preset = (raw?.preset ?? '').toString().trim();
  if (!preset || preset.toLowerCase() === 'none') return null;
  const dur = positiveOrNull(raw?.durationSeconds);
  if (dur === null) return null;
  return { preset, durationSeconds: round3(dur) };
}

/** Ducking shape: how far to attenuate music under voice, and the ramp length. */
const DUCK_GAIN = 0.3;
const DUCK_RAMP_SECONDS = 0.15;

/**
 * Build the absolute-output-time ducking curve from the project: when ANY clip is
 * flagged `duckUnderVoice` (music/soundtrack), gather the VOICE windows — audible
 * source clips (audio, or video carrying audio) that are NOT themselves duck targets
 * and not muted — and emit a gain curve that dips to {@link DUCK_GAIN} during each
 * voice window with a {@link DUCK_RAMP_SECONDS} linear ramp in/out, returning to 1.0
 * elsewhere. Returns null when there is no music-to-duck or no voice (→ no curve →
 * audio unchanged). The worker applies this curve ONLY to clips flagged
 * `duckUnderVoice`, so voice is never ducked against itself.
 */
function buildAudioDucking(
  project: EditorProject,
  durationSeconds: number
): { tSeconds: number; gain: number }[] | null {
  let hasDuckTarget = false;
  const voiceWindows: Array<{ start: number; end: number }> = [];

  const allTracks = project.tracks ?? [];
  for (const track of allTracks) {
    // A non-audible track (hidden / muted / non-soloed while another is soloed) produces no
    // audio in the output, so it is neither a duck target nor a voice source.
    const trackAudible = isTrackAudible(track, allTracks);
    for (const clip of track.clips ?? []) {
      if (clip.kind !== 'audio' && clip.kind !== 'video') continue;
      const start = finiteOr(clip.startSeconds, 0);
      const end = finiteOr(clip.endSeconds, start);
      if (end <= start) continue;
      if (!trackAudible) continue;

      if (clip.duckUnderVoice) {
        hasDuckTarget = true;
        continue; // a duck TARGET is not a voice source.
      }
      // Voice/dialogue = an audible non-music source clip (muted/zero-volume → not voice).
      if (clip.muted) continue;
      if ((clip.volume ?? 1) <= 0) continue;
      voiceWindows.push({ start, end });
    }
  }

  if (!hasDuckTarget || !voiceWindows.length) return null;

  // Merge overlapping/adjacent voice windows so the duck curve is monotonic & clean.
  voiceWindows.sort((a, b) => a.start - b.start);
  const merged: Array<{ start: number; end: number }> = [];
  for (const w of voiceWindows) {
    const last = merged[merged.length - 1];
    if (last && w.start <= last.end + DUCK_RAMP_SECONDS) {
      last.end = Math.max(last.end, w.end);
    } else {
      merged.push({ start: w.start, end: w.end });
    }
  }

  // Emit keyframes: 1.0 baseline, ramp down to DUCK_GAIN over the window, ramp back.
  const points: Array<{ tSeconds: number; gain: number }> = [];
  const push = (t: number, gain: number): void => {
    const tt = round3(Math.max(0, Math.min(durationSeconds, t)));
    const prev = points[points.length - 1];
    if (prev && Math.abs(prev.tSeconds - tt) < 1e-4) {
      prev.gain = gain; // collapse coincident times (keep the latest, e.g. dip over ramp-up)
      return;
    }
    points.push({ tSeconds: tt, gain });
  };

  push(0, 1);
  for (const w of merged) {
    const downStart = Math.max(0, w.start - DUCK_RAMP_SECONDS);
    push(downStart, 1);
    push(w.start, DUCK_GAIN);
    push(w.end, DUCK_GAIN);
    push(Math.min(durationSeconds, w.end + DUCK_RAMP_SECONDS), 1);
  }
  push(durationSeconds, 1);

  // Guard: need ≥2 distinct points to interpolate; otherwise no useful curve.
  return points.length >= 2 ? points : null;
}

/**
 * Convert the editor's center-offset transform to the worker's normalized rect.
 *
 * EditorTransform: `x`/`y` are PERCENT offsets of the canvas from centre, `scale`
 * is a multiplier on a full-cover box (`scaleX`/`scaleY` independently scale width/
 * height, falling back to `scale` via {@link effectiveScaleXY}), `rotation` deg,
 * `opacity` 0..1.
 * RenderTransform: normalized top-left `x`/`y` + `width`/`height` (fraction of
 * frame), `rotationDeg`, `opacity`, `fit`. A full-cover box at scale 1 with no
 * offset/rotation and opacity 1 is the renderer default → return undefined so the
 * clip draws identically to the baseline (matches WebavCanvasService.drawCover).
 *
 * NON-UNIFORM (sx ≠ sy): the box becomes sx×sy of the frame and `fit:'stretch'` so
 * the worker's resolveRect draws straight into that box (no cover re-fit back to
 * square) — mirroring the preview compositor's `context.scale(sx, sy)` on the
 * cover-filled element. UNIFORM (sx === sy): `fit:'cover'`, byte-identical to today.
 */
function mapTransform(
  transform: EditorTransform | null | undefined,
  _width: number,
  _height: number,
  fitMode: 'cover' | 'contain' | 'stretch' = 'cover',
): RenderTransform | undefined {
  if (!transform) {
    return fitMode !== 'cover'
      ? { x: 0, y: 0, width: 1, height: 1, fit: fitMode }
      : undefined;
  }
  const offsetXPercent = finiteOr(transform.x, 0);
  const offsetYPercent = finiteOr(transform.y, 0);
  // Independent per-axis scale via the SHARED helper, so the rect the worker draws
  // matches the preview's per-axis `context.scale(sx, sy)` exactly. A clip with no
  // scaleX/scaleY yields sx === sy === scale → the uniform branch below = today.
  const { sx, sy } = effectiveScaleXY(transform);
  const boxWidth = Math.max(0.1, finiteOr(sx, 1));
  const boxHeight = Math.max(0.1, finiteOr(sy, 1));
  const nonUniform = Math.abs(boxWidth - boxHeight) > 1e-6;
  const rotationDeg = finiteOr(transform.rotation, 0);
  const opacity = clamp01(finiteOr(transform.opacity, 1));

  const isIdentity =
    offsetXPercent === 0 && offsetYPercent === 0 &&
    boxWidth === 1 && boxHeight === 1 &&
    rotationDeg === 0 && opacity === 1;
  if (isIdentity) {
    return fitMode !== 'cover'
      ? { x: 0, y: 0, width: 1, height: 1, fit: fitMode }
      : undefined;
  }

  // Full-cover box is the whole frame; per-axis scale grows/shrinks it about the
  // centre; the percent offset shifts the centre. Convert centre+size → top-left rect.
  const centreX = 0.5 + offsetXPercent / 100;
  const centreY = 0.5 + offsetYPercent / 100;

  const out: RenderTransform = {
    x: round3(centreX - boxWidth / 2),
    y: round3(centreY - boxHeight / 2),
    width: round3(boxWidth),
    height: round3(boxHeight),
    // Uniform → 'cover' (re-fits about centre, unchanged from today). Non-uniform →
    // 'stretch' so resolveRect uses the box dimensions directly and the axes scale
    // independently instead of being cover-cropped back to a square.
    fit: nonUniform ? 'stretch' : fitMode
  };
  if (rotationDeg !== 0) out.rotationDeg = round3(rotationDeg);
  if (opacity !== 1) out.opacity = round3(opacity);
  return out;
}

/** text/caption clip → RenderCaption (the fields the worker's drawCaption reads). */
function buildCaption(clip: EditorClip, start: number, end: number, index: number): RenderCaption | null {
  const text = (clip.text ?? clip.title ?? '').toString().trim();
  if (!text) return null;

  const style: EditorTextStyle = clip.textStyle ?? {};
  const { x, y } = captionAnchor(clip.transform);

  const caption: RenderCaption = {
    id: clip.id || `caption-${index}`,
    text,
    startSeconds: round3(start),
    endSeconds: round3(end),
    x,
    y,
    bold: (style.fontWeight ?? 400) >= 600,
    align: style.align ?? 'center',
    textStyleKeyframes: clip.textStyleKeyframes?.map(point => ({ ...point })) ?? null,
  };

  // KEYFRAME + ENTER/EXIT animation (Animate tool) — carried as PLAIN objects so the
  // worker resolves them per frame through the SHARED resolver (preview == export),
  // exactly like buildMediaClip does. Channels are editor-space (x/y percent, scale
  // multiplier, rotation deg, opacity 0..1). Absent → omitted, so a static caption
  // renders byte-for-byte as today. The base transform rides along (centre offset +
  // scale + rotation + opacity) so the keyframes fold over the caption's authored pose.
  const captionKeyframes = mapTransformKeyframes(effectiveMotionTrackingKeyframes(clip));
  if (captionKeyframes) caption.keyframes = captionKeyframes;
  const captionAnimateIn = mapAnimateSpec(clip.animateIn);
  if (captionAnimateIn) caption.animateIn = captionAnimateIn;
  const captionAnimateOut = mapAnimateSpec(clip.animateOut);
  if (captionAnimateOut) caption.animateOut = captionAnimateOut;
  // Carry the base center-anchored pose whenever it's non-identity — so a scaled/rotated/
  // faded caption renders the same in export as preview, whether or not it's animated.
  // Identity → mapCenterTransform returns null → omitted → worker's static fast path
  // (byte-identical). For a static caption the worker applies only scale/rotation/opacity
  // (the x/y offset is already folded into the anchor), so an offset-only caption is
  // still drawn on the fast path.
  const captionBase = mapCenterTransform(clip.transform);
  if (captionBase) caption.baseTransform = captionBase;

  const sizePercent = positiveOr(style.sizePercent, 1);
  if (Number.isFinite(style.fontSize as number)) {
    // EditorTextStyle.fontSize is at a 1080-wide reference; scale to the output
    // height-based default the worker uses (it falls back to outH*0.05 ≈ 96 on a
    // 1920-tall canvas). Keep the author's px (× sizePercent); the worker scales
    // by output if it wants — passing fontSizePx pins the intended size.
    caption.fontSizePx = Math.max(8, Math.round((style.fontSize as number) * sizePercent));
  }
  // Font family: Urdu language wins (script correctness); else the picked display font.
  // fontSpecsForCaptions() then ships the matching woff2 spec so the worker loads it.
  if (style.language === 'ur') caption.fontFamily = 'urdu';
  else if (style.fontFamily && style.fontFamily.trim()) caption.fontFamily = style.fontFamily.trim();
  const color = (clip.color || '').trim();
  if (color) caption.color = color;
  if (style.background) {
    caption.backgroundColor = style.background;
    if (Number.isFinite(style.backgroundOpacity as number)) {
      caption.backgroundOpacity = clamp01(style.backgroundOpacity as number);
    }
    if (Number.isFinite(style.backgroundRoundness as number)) caption.backgroundRoundness = clamp01(style.backgroundRoundness as number);
    if (Number.isFinite(style.backgroundWidth as number)) caption.backgroundWidth = Math.max(0.25, Math.min(2, style.backgroundWidth as number));
    if (Number.isFinite(style.backgroundHeight as number)) caption.backgroundHeight = Math.max(0.25, Math.min(2, style.backgroundHeight as number));
    if (Number.isFinite(style.backgroundOffsetX as number)) caption.backgroundOffsetX = Math.max(-100, Math.min(100, style.backgroundOffsetX as number));
    if (Number.isFinite(style.backgroundOffsetY as number)) caption.backgroundOffsetY = Math.max(-100, Math.min(100, style.backgroundOffsetY as number));
  }
  // Authored caption styling (Text-tool presets Clean/Outline/Pop/Boxed). Each is carried
  // ONLY when the author set it, so a plain/auto caption (no textStyle.* ) is byte-identical.
  if (Number.isFinite(style.fontWeight as number)) caption.fontWeight = style.fontWeight as number;
  if (style.fontStyle) caption.fontStyle = style.fontStyle;
  if (Number.isFinite(style.lineHeight as number)) caption.lineHeight = style.lineHeight as number;
  if (Number.isFinite(style.curve as number)) caption.curve = Math.max(-360, Math.min(360, style.curve as number));
  if (Number.isFinite(style.blendOpacity as number)) caption.blendOpacity = clamp01(style.blendOpacity as number);
  // stroke: a color → that outline; null → NO stroke; undefined → leave off (worker default dark stroke).
  if (style.stroke !== undefined) caption.strokeColor = style.stroke;
  if (Number.isFinite(style.strokeWidth as number)) caption.strokeWidthPx = Math.max(0, style.strokeWidth as number);
  // Rich fills (gradient / neon glow / 3D shadow) — carried ONLY when authored, so a plain
  // caption stays byte-identical. The worker paints these in drawCaptionStatic (preview parity).
  if (style.gradient && style.gradient.from && style.gradient.to) {
    caption.gradient = { from: style.gradient.from, to: style.gradient.to, via: style.gradient.via ?? null };
  }
  if (style.glow && style.glow.color) {
    caption.glow = {
      color: style.glow.color,
      strength: style.glow.strength,
      range: style.glow.range,
      offsetX: style.glow.offsetX,
      offsetY: style.glow.offsetY,
    };
  }
  if (style.shadow && style.shadow.color) {
    caption.shadow = {
      color: style.shadow.color,
      opacity: style.shadow.opacity,
      blur: style.shadow.blur,
      distance: style.shadow.distance,
      angle: style.shadow.angle,
    };
  }
  if (style.shadow3d && style.shadow3d.color) {
    caption.shadow3d = { color: style.shadow3d.color, dx: style.shadow3d.dx, dy: style.shadow3d.dy };
  }
  // Bubble SHAPE + letter-by-letter animation — carried only when authored (byte-identical otherwise).
  if (style.bubble && style.bubble.shape && style.bubble.color) {
    caption.bubble = { ...style.bubble };
  }
  if (style.textAnim && style.textAnim.type) {
    caption.textAnim = { type: style.textAnim.type, speed: style.textAnim.speed };
  }
  return caption;
}

/** Normalized caption anchor from the editor's center-offset transform (else the editor default). */
function captionAnchor(transform: EditorTransform | null | undefined): { x: number; y: number } {
  if (transform && (Number.isFinite(transform.x) || Number.isFinite(transform.y))) {
    return {
      x: clamp01(0.5 + finiteOr(transform.x, 0) / 100),
      y: clamp01(0.5 + finiteOr(transform.y, 0) / 100)
    };
  }
  // WebavCanvasService draws captions without an explicit transform at y = 0.78.
  return { x: 0.5, y: 0.78 };
}

/** sticker clip backed by an image asset → RenderSticker (image overlay). */
function buildImageSticker(
  clip: EditorClip,
  start: number,
  end: number,
  sourceId: string,
  index: number,
  width: number,
  height: number
): RenderSticker {
  const sticker: RenderSticker = {
    id: clip.id || `sticker-${index}`,
    sourceId,
    startSeconds: round3(start),
    endSeconds: round3(end),
    animation: stickerAnimation(clip)
  };
  const transform = mapTransform(clip.transform, width, height);
  // Image stickers normally letterbox (`contain`); but a non-uniformly resized one
  // already carries `fit:'stretch'` from mapTransform — keep that so its independent
  // width/height survive instead of being re-fit back to the source aspect.
  if (transform) sticker.transform = { ...transform, fit: transform.fit === 'stretch' ? 'stretch' : 'contain' };

  // KEYFRAME + ENTER/EXIT animation (Animate tool) — same plain-object carry as
  // buildMediaClip/buildCaption; the worker resolves them per frame via the SHARED
  // resolver so an animated image sticker animates in export == preview. Absent →
  // omitted (static sticker unchanged).
  const stKeyframes = mapTransformKeyframes(effectiveMotionTrackingKeyframes(clip));
  if (stKeyframes) sticker.keyframes = stKeyframes;
  const stAnimateIn = mapAnimateSpec(clip.animateIn);
  if (stAnimateIn) sticker.animateIn = stAnimateIn;
  const stAnimateOut = mapAnimateSpec(clip.animateOut);
  if (stAnimateOut) sticker.animateOut = stAnimateOut;
  return sticker;
}

/** Optional entrance/exit animation hint surfaced from clip metadata. */
function stickerAnimation(clip: EditorClip): string | null {
  const meta = clip.metadata as Record<string, unknown> | undefined;
  const value = meta?.['animation'];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * Primitive sticker clip (no image) → RenderEffect carrying its params
 * (family:'sticker'). The clip TRANSFORM is carried as the worker's center-anchored
 * sticker params (matching the preview's `withClipTransform`: percent offset from
 * centre, scale multiplier, rotation, opacity) so the rendered primitive lands in
 * the same place the editor shows it. Omitting the transform → identity placement
 * (centred, full-scale, opaque), which is the editor's default sticker pose.
 */
function buildStickerEffect(clip: EditorClip, start: number, end: number, index: number): RenderEffect {
  const sticker: EditorStickerParams | null = clip.sticker ?? null;
  const tf = clip.transform ?? null;
  const params: Record<string, number | string | boolean | null> = {
    family: 'sticker',
    primitive: sticker?.primitive ?? 'badge',
    color: sticker?.color ?? null,
    color2: sticker?.color2 ?? null,
    size: numOrNull(sticker?.size),
    angle: numOrNull(sticker?.angle),
    text: sticker?.text ?? clip.text ?? clip.title ?? null,
    // Modern Library arrows/symbols all use primitive:'shape' and distinguish
    // their actual vector geometry through these declarative fields. Dropping
    // them makes the worker use its default essential-circle (the red dot seen
    // in exports) even though the live canvas correctly shows the chosen icon.
    variant: sticker?.variant ?? null,
    style: sticker?.style ?? null,
    strokeWidth: numOrNull(sticker?.strokeWidth),
    // Center-anchored clip transform (same model the preview applies to stickers).
    stickerOffsetXPercent: numOrNull(tf?.x),
    stickerOffsetYPercent: numOrNull(tf?.y),
    stickerScale: numOrNull(tf?.scale),
    // Per-axis scale (non-uniform edge-handle resize); each falls back to stickerScale in
    // the worker, so a uniformly-scaled sticker is unchanged. Preview already applies these.
    stickerScaleX: numOrNull(tf?.scaleX),
    stickerScaleY: numOrNull(tf?.scaleY),
    stickerRotationDeg: numOrNull(tf?.rotation),
    opacity: numOrNull(tf?.opacity)
  };
  const fx: RenderEffect = {
    id: clip.id || `sticker-fx-${index}`,
    type: sticker?.primitive ?? 'sticker',
    startSeconds: round3(start),
    endSeconds: round3(end),
    params
  };

  // KEYFRAME + ENTER/EXIT animation (Animate tool) — carried so the worker folds them
  // OVER the static center-anchored `sticker*` params each frame via the SHARED resolver
  // (preview == export). The base pose is already in the `sticker*` params above, so the
  // resolver's base for the fold is that same pose. Absent → omitted (static unchanged).
  const fxKeyframes = mapTransformKeyframes(effectiveMotionTrackingKeyframes(clip));
  if (fxKeyframes) fx.keyframes = fxKeyframes;
  const fxAnimateIn = mapAnimateSpec(clip.animateIn);
  if (fxAnimateIn) fx.animateIn = fxAnimateIn;
  const fxAnimateOut = mapAnimateSpec(clip.animateOut);
  if (fxAnimateOut) fx.animateOut = fxAnimateOut;
  return fx;
}

/**
 * Map an EditorTransform to the worker's EDITOR-space center-anchored channel bag
 * (carried verbatim for caption/sticker keyframe resolution — NOT the normalized rect
 * `mapTransform` builds). Null transform → null (identity centre pose). Per-axis scale
 * is passed through as-is; the resolver's `effectiveScaleXY`-equivalent fallback applies.
 */
function mapCenterTransform(transform: EditorTransform | null | undefined): RenderCenterTransform | null {
  if (!transform) return null;
  const out: RenderCenterTransform = {};
  const x = numOrNull(transform.x);
  if (x !== null && x !== 0) out.x = round3(x);
  const y = numOrNull(transform.y);
  if (y !== null && y !== 0) out.y = round3(y);
  const scale = numOrNull(transform.scale);
  if (scale !== null && scale !== 1) out.scale = round3(scale);
  const scaleX = numOrNull(transform.scaleX);
  if (scaleX !== null) out.scaleX = round3(scaleX);
  const scaleY = numOrNull(transform.scaleY);
  if (scaleY !== null) out.scaleY = round3(scaleY);
  const rotation = numOrNull(transform.rotation);
  if (rotation !== null && rotation !== 0) out.rotationDeg = round3(rotation);
  const opacity = numOrNull(transform.opacity);
  if (opacity !== null && opacity !== 1) out.opacity = round3(opacity);
  return Object.keys(out).length ? out : null;
}

/** effect/transition/beat clip → RenderEffect (family-tagged + all primitive params). */
function buildEffect(
  clip: EditorClip,
  start: number,
  end: number,
  index: number,
  effectSceneSourceIdByUrl: ReadonlyMap<string, string> = new Map(),
): RenderEffect {
  const family = clip.kind; // 'effect' | 'transition' | 'beat'
  const primitiveParams: EditorEffectParams | EditorTransitionParams | EditorBeatParams | null =
    clip.effect ?? clip.transition ?? clip.beat ?? null;

  const params: NonNullable<RenderEffect['params']> = {
    family,
    primitive: primitiveParams?.primitive ?? clip.kind,
    color: primitiveParams?.color ?? clip.color ?? null,
    color2: (primitiveParams as { color2?: string } | null)?.color2 ?? null,
    opacity: numOrNull((primitiveParams as { opacity?: number } | null)?.opacity),
    speed: numOrNull((primitiveParams as { speed?: number } | null)?.speed),
    size: numOrNull((primitiveParams as { size?: number } | null)?.size),
    variant: numOrNull((primitiveParams as { variant?: number } | null)?.variant),
    seed: numOrNull((primitiveParams as { seed?: number } | null)?.seed),
    density: numOrNull((primitiveParams as { density?: number } | null)?.density),
    depth: numOrNull((primitiveParams as { depth?: number } | null)?.depth),
    symmetry: numOrNull((primitiveParams as { symmetry?: number } | null)?.symmetry),
    material: (primitiveParams as { material?: string } | null)?.material ?? null,
    audioMap: (primitiveParams as { audioMap?: string } | null)?.audioMap ?? null,
    layout: (primitiveParams as { layout?: string } | null)?.layout ?? null,
    angle: numOrNull((primitiveParams as { angle?: number } | null)?.angle),
    blend: (primitiveParams as { blend?: string } | null)?.blend ?? null,
    intensity: numOrNull((clip.effect as EditorEffectParams | null)?.intensity),
    scope: (primitiveParams as { scope?: string } | null)?.scope ?? null,
    target: (primitiveParams as { target?: string } | null)?.target ?? null,
    detectionTarget: (primitiveParams as { detectionTarget?: string } | null)?.detectionTarget ?? null,
    requiresPerson: (primitiveParams as { requiresPerson?: boolean } | null)?.requiresPerson ?? null,
    glId: (primitiveParams as { glId?: string } | null)?.glId ?? null,
    glUniforms: (primitiveParams as { glUniforms?: Record<string, number | number[]> } | null)?.glUniforms ?? null,
    sceneConcept: numOrNull((clip.effect as EditorEffectParams | null)?.sceneConcept),
    filterId: (clip.effect as EditorEffectParams | null)?.filterId ?? null,
    filterStrength: numOrNull((clip.effect as EditorEffectParams | null)?.filterStrength),
    brightness: numOrNull((clip.effect as EditorEffectParams | null)?.brightness),
    contrast: numOrNull((clip.effect as EditorEffectParams | null)?.contrast),
    saturation: numOrNull((clip.effect as EditorEffectParams | null)?.saturation),
    temperature: numOrNull((clip.effect as EditorEffectParams | null)?.temperature),
    blur: numOrNull((clip.effect as EditorEffectParams | null)?.blur),
    vignette: numOrNull((clip.effect as EditorEffectParams | null)?.vignette),
    hue: numOrNull((clip.effect as EditorEffectParams | null)?.hue)
  };

  const sceneProgram = cloneEffectSceneProgram(
    (clip.effect as EditorEffectParams | null)?.sceneProgram,
  );
  const sceneAssetUrl = (clip.effect as EditorEffectParams | null)?.sceneAssetUrl?.trim() ?? '';
  const sceneSourceId = sceneAssetUrl
    ? effectSceneSourceIdByUrl.get(sceneAssetUrl)
    : undefined;

  return {
    id: clip.id || `effect-${index}`,
    type: primitiveParams?.primitive ?? family,
    startSeconds: round3(start),
    endSeconds: round3(end),
    params,
    ...(sceneSourceId ? { sceneSourceId } : {}),
    ...(sceneProgram ? { sceneProgram } : {}),
  };
}

/**
 * Rebuild the downloaded program from its allow-listed fields. This both strips
 * prototypes/extraneous values and guarantees that RenderJob.postMessage never receives
 * functions, accessors, DOM values, or other non-structured-clone data from a catalog.
 */
function cloneEffectSceneProgram(value: unknown): EffectSceneProgram | null {
  if (!isEffectSceneProgram(value)) return null;
  const cpuParams = value.cpu.params;
  const params: NonNullable<EffectSceneProgram['cpu']['params']> = {};
  if (cpuParams) {
    for (const [key, entry] of Object.entries(cpuParams)) {
      if (!/^[A-Za-z_][A-Za-z0-9_]{0,63}$/.test(key)) continue;
      if (
        entry === null
        || typeof entry === 'boolean'
        || (typeof entry === 'number' && Number.isFinite(entry))
        || (typeof entry === 'string' && entry.length <= 256)
      ) {
        params[key] = entry;
      } else if (
        Array.isArray(entry)
        && entry.length <= 16
        && entry.every(component => typeof component === 'number' && Number.isFinite(component))
      ) {
        params[key] = [...entry];
      }
    }
  }

  return {
    schemaVersion: 1,
    id: value.id,
    surface: value.surface,
    gpu: {
      language: 'glsl-es-300',
      source: value.gpu.source,
      uniforms: value.gpu.uniforms.map(uniform => ({
        name: uniform.name,
        type: uniform.type,
        default: [...uniform.default],
      })),
    },
    cpu: {
      kind: 'canvas-primitive',
      primitive: value.cpu.primitive,
      ...(Object.keys(params).length ? { params } : {}),
    },
  };
}

/**
 * MOTION clip (kind:'motion' on an 'animation' track) → a minimal motion entry carrying
 * its window + sanitized keyframes (channels are editor-space, authored around IDENTITY:
 * x/y percent, scale multiplier, rotation deg, opacity 0..1 — the same space the SHARED
 * resolver/composeMotionDelta operates in). Returns null when the clip has no usable
 * keyframes, so an empty motion never bloats the job (the compositor's fast path stays
 * engaged whenever no motion can actually move the base frame).
 */
function buildMotion(
  clip: EditorClip,
  start: number,
  end: number
): { startSeconds: number; endSeconds: number; keyframes: TransformKeyframe[] } | null {
  const keyframes = mapTransformKeyframes(effectiveMotionTrackingKeyframes(clip));
  if (!keyframes) return null;
  return { startSeconds: round3(start), endSeconds: round3(end), keyframes };
}

// ---------------------------------------------------------------------------
// Kind mapping
// ---------------------------------------------------------------------------

function mapTrackKind(kind: string): RenderTrackKind | null {
  if (kind === 'video') return 'video';
  if (kind === 'overlay') return 'overlay';
  if (kind === 'audio') return 'audio';
  return null; // caption / effect / beat tracks carry no media clips.
}

function mapSourceKind(kind: string): RenderSourceKind | null {
  if (kind === 'video') return 'video';
  if (kind === 'image' || kind === 'sticker') return 'image';
  if (kind === 'audio') return 'audio';
  return null; // text / caption / effect — no media source.
}

function isImageAsset(asset: EditorAsset | undefined): boolean {
  return !!asset && (asset.kind === 'image' || asset.kind === 'sticker');
}

// ---------------------------------------------------------------------------
// Small numeric / type helpers (kept local + pure)
// ---------------------------------------------------------------------------

function asBlob(value: unknown): Blob | null {
  return typeof Blob !== 'undefined' && value instanceof Blob ? value : null;
}

function finiteOr(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function positiveOr(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
}

function positiveOrNull(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

function numOrNull(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** Cover-thumbnail count clamped to 1..15 (default 1 for any non-positive input). */
function clampThumbnailCount(count: number | null | undefined): number {
  const n = Math.trunc(Number(count ?? 1));
  if (n === 0) return 0;
  return Number.isFinite(n) && n > 0 ? Math.min(15, n) : 1;
}

function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function generateId(): string {
  return globalThis.crypto?.randomUUID?.() || `editor-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
